import React, { useState, useEffect } from 'react';
import {
	Share2,
	Edit3,
	Check,
	X,
	Globe,
	Monitor,
	User,
	Lock,
	Eye,
	EyeOff,
} from 'lucide-react';
import Card from '../../../components/ui/card';
import Button from '../../../components/ui/button';
import Input from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';

const SocialMediaCard = ({ client, clientDataHook }) => {
	const { updateCard, isCardEditMode, toggleCardEditMode, cancelCardEdit, isOnboarding, refreshData, isLoading } = clientDataHook;
	const [showCrmFields, setShowCrmFields] = useState({});
	const cardName = 'socialMedia';
	const isEditing = isOnboarding || isCardEditMode(cardName);
	
	// Refresh data when component mounts (for onboarding)
	useEffect(() => {
		if (isOnboarding && refreshData) {
			console.log('🟦 SocialMediaCard - Component mounted, refreshing data');
			refreshData();
		}
	}, [isOnboarding]);

	// Form state for onboarding mode
	const [formValues, setFormValues] = useState({
		instagramLink: client?.instagramLink || '',
		facebookLink: client?.facebookLink || '',
		googleBusinessUrl: client?.googleBusinessUrl || '',
		linkedinLink: client?.linkedinLink || '',
		websiteUrl: client?.websiteUrl || '',
		websitePlatform: client?.websitePlatform || '',
		socialMediaCredentials: client?.socialMediaCredentials || '',
		websiteCredentials: client?.websiteCredentials || '',
		crmName: client?.crmName || '',
		crmUsername: client?.crmUsername || '',
		crmPassword: client?.crmPassword || ''
	});

	// Update form values when client data changes
	useEffect(() => {
		setFormValues({
			instagramLink: client?.instagramLink || '',
			facebookLink: client?.facebookLink || '',
			googleBusinessUrl: client?.googleBusinessUrl || '',
			linkedinLink: client?.linkedinLink || '',
			websiteUrl: client?.websiteUrl || '',
			websitePlatform: client?.websitePlatform || '',
			socialMediaCredentials: client?.socialMediaCredentials || '',
			websiteCredentials: client?.websiteCredentials || '',
			crmName: client?.crmName || '',
			crmUsername: client?.crmUsername || '',
			crmPassword: client?.crmPassword || ''
		});
	}, [client]);

	const fields = [
		{
			key: 'instagramLink',
			label: 'Instagram Link',
			type: 'url',
			value: client?.instagramLink || '',
			icon: Share2,
			placeholder: 'https://instagram.com/yourusername',
		},
		{
			key: 'facebookLink',
			label: 'Facebook Link',
			type: 'url',
			value: client?.facebookLink || '',
			icon: Share2,
			placeholder: 'https://facebook.com/yourpage',
		},
		{
			key: 'googleBusinessUrl',
			label: 'Google Business Profile Link',
			type: 'url',
			value: client?.googleBusinessUrl || '',
			icon: Globe,
			placeholder: 'https://business.google.com/yourprofile',
		},
		{
			key: 'linkedinLink',
			label: 'LinkedIn Link',
			type: 'url',
			value: client?.linkedinLink || '',
			icon: Share2,
			placeholder: 'https://linkedin.com/in/yourprofile',
		},
		{
			key: 'socialMediaCredentials',
			label: 'Social Media Login Credentials',
			type: 'textarea',
			value: client?.socialMediaCredentials || '',
			icon: Lock,
			placeholder: 'Store your social media account usernames and passwords securely here...\n\nExample:\nInstagram: @username / password123\nFacebook: user@email.com / password456',
			isSensitive: true,
		},
		{
			key: 'websiteUrl',
			label: 'Personal Website',
			type: 'url',
			value: client?.websiteUrl || '',
			icon: Globe,
			placeholder: 'https://yourwebsite.com',
		},
		{
			key: 'websitePlatform',
			label: 'Website Platform',
			type: 'text',
			value: client?.websitePlatform || '',
			icon: Globe,
			placeholder: 'WordPress, Wix, Squarespace, etc.',
		},
		{
			key: 'websiteCredentials',
			label: 'Website/CMS Admin Credentials',
			type: 'textarea',
			value: client?.websiteCredentials || '',
			icon: Lock,
			placeholder: 'Store your website/CMS admin login information securely here...\n\nExample:\nAdmin URL: https://yoursite.com/wp-admin\nUsername: admin\nPassword: password123',
			isSensitive: true,
		},
		{
			key: 'crmName',
			label: 'CRM Software',
			type: 'text',
			value: client?.crmName || '',
			icon: Monitor,
			placeholder: 'Salesforce, HubSpot, etc.',
		},
		{
			key: 'crmUsername',
			label: 'CRM Username',
			type: 'text',
			value: client?.crmUsername || '',
			icon: User,
			placeholder: 'Your CRM username',
			isSensitive: true,
		},
		{
			key: 'crmPassword',
			label: 'CRM Password',
			type: 'password',
			value: client?.crmPassword || '',
			icon: Lock,
			placeholder: 'Your CRM password (for documentation)',
			isSensitive: true,
		},
	];

	
	// Handle form field changes for onboarding
	const handleFormFieldChange = (fieldKey, value) => {
		setFormValues(prev => ({
			...prev,
			[fieldKey]: value
		}));
	};
	
	// Handle save for onboarding mode
	const handleOnboardingSave = async () => {
		// Validate URLs before saving
		for (const [key, value] of Object.entries(formValues)) {
			const field = fields.find(f => f.key === key);
			if (field?.type === 'url' && value && value.trim() !== '') {
				try {
					new URL(value);
				} catch (error) {
					alert(`Please enter a valid URL for ${field.label} (including http:// or https://)`);
					return false;
				}
			}
		}
		
		const success = await updateCard(formValues);
		return success;
	};

	// Handle save for profile mode (card-wide editing)
	const handleSave = async () => {
		// Validate URLs before saving
		for (const [key, value] of Object.entries(formValues)) {
			const field = fields.find(f => f.key === key);
			if (field?.type === 'url' && value && value.trim() !== '') {
				try {
					new URL(value);
				} catch (error) {
					alert(`Please enter a valid URL for ${field.label} (including http:// or https://)`);
					return false;
				}
			}
		}
		
		const success = await updateCard(formValues);
		if (success) {
			toggleCardEditMode(cardName); // Exit edit mode
		}
	};

	// Handle cancel for profile mode (card-wide editing)
	const handleCancel = () => {
		// Reset form values to original client data
		setFormValues({
			instagramLink: client?.instagramLink || '',
			facebookLink: client?.facebookLink || '',
			googleBusinessUrl: client?.googleBusinessUrl || '',
			linkedinLink: client?.linkedinLink || '',
			socialMediaCredentials: client?.socialMediaCredentials || '',
			websiteUrl: client?.websiteUrl || '',
			websitePlatform: client?.websitePlatform || '',
			websiteCredentials: client?.websiteCredentials || '',
			crmName: client?.crmName || '',
			crmUsername: client?.crmUsername || '',
			crmPassword: client?.crmPassword || ''
		});
		cancelCardEdit(cardName);
	};

	return (
		<Card>
			{!isOnboarding && (
				<Card.Header className='flex flex-row items-center justify-center space-y-0 pb-4'>
					<div className='space-y-1 text-center'>
						<Card.Title className='flex items-center gap-2 text-lg font-montserrat-medium text-gray-900 justify-center'>
							<Share2 className='h-5 w-5' />
							Social Media & Software
						</Card.Title>
						<Card.Description className='text-sm text-gray-600'>
							Your social media profiles, website, and CRM information.
						</Card.Description>
					</div>
					{!isEditing && (
						<Button
							size='sm'
							variant='outline'
							onClick={() => toggleCardEditMode(cardName)}
							className='border-gray-300 text-gray-600 hover:bg-gray-50 ml-4'>
							<Edit3 className='h-4 w-4 mr-2' />
							Edit
						</Button>
					)}
				</Card.Header>
			)}
			<Card.Content className='space-y-4'>
				{fields.map((field) => (
					<div key={field.key} className='space-y-2'>
						<label className='text-sm font-semibold text-gray-800'>
							{field.label}
						</label>
						
						{isEditing ? (
							field.isSensitive ? (
								<div className='relative'>
									{field.type === 'textarea' ? (
										<>
											<Textarea
												value={formValues[field.key] || ''}
												onChange={(e) => handleFormFieldChange(field.key, e.target.value)}
												placeholder={field.placeholder}
												className='w-full min-h-[100px] pr-10'
												style={{ fontFamily: showCrmFields[field.key] ? 'monospace' : 'inherit' }}
											/>
											<Button
												type='button'
												onClick={() => setShowCrmFields(prev => ({ ...prev, [field.key]: !prev[field.key] }))}
												size='sm'
												variant='ghost'
												className='absolute right-2 top-2 h-6 w-6 p-0 text-black hover:text-gray-700'>
												{showCrmFields[field.key] ? <EyeOff className='h-7 w-7' /> : <Eye className='h-7 w-7' />}
											</Button>
										</>
									) : (
										<>
											<Input
												type={showCrmFields[field.key] ? 'text' : (field.type === 'password' ? 'password' : 'password')}
												value={formValues[field.key] || ''}
												onChange={(e) => handleFormFieldChange(field.key, e.target.value)}
												placeholder={field.placeholder}
												className='w-full pr-10'
											/>
											<Button
												type='button'
												onClick={() => setShowCrmFields(prev => ({ ...prev, [field.key]: !prev[field.key] }))}
												size='sm'
												variant='ghost'
												className='absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors'
												title={showCrmFields[field.key] ? 'Hide' : 'Show'}>
												{showCrmFields[field.key] ? <EyeOff className='h-7 w-7' /> : <Eye className='h-7 w-7' />}
											</Button>
										</>
									)}
								</div>
							) : (
								field.type === 'textarea' ? (
									<Textarea
										value={formValues[field.key] || ''}
										onChange={(e) => handleFormFieldChange(field.key, e.target.value)}
										placeholder={field.placeholder}
										className='w-full min-h-[100px]'
									/>
								) : (
									<Input
										type={field.type}
										value={formValues[field.key] || ''}
										onChange={(e) => handleFormFieldChange(field.key, e.target.value)}
										placeholder={field.placeholder}
										className='w-full'
									/>
								)
							)
						) : (
							<div className='flex items-start gap-2'>
								<field.icon className='h-4 w-4 text-gray-500 mt-0.5' />
								<div className='flex-1'>
									<span className='text-sm font-montserrat-regular text-gray-900 break-all'>
										{field.value ? (
											field.type === 'url' ? (
												<a 
													href={field.value} 
													target='_blank' 
													rel='noopener noreferrer'
													className='text-blue-600 hover:text-blue-800 underline'>
													{field.value}
												</a>
											) : field.isSensitive ? (
												<div className='flex items-center gap-2'>
													<span>
														{showCrmFields[field.key] ? field.value : '•'.repeat(field.value.length || 8)}
													</span>
													<Button
														onClick={() => setShowCrmFields(prev => ({ ...prev, [field.key]: !prev[field.key] }))}
														size='sm'
														variant='ghost'
														className='h-7 w-7 p-0 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors'
														title={showCrmFields[field.key] ? 'Hide' : 'Show'}>
														{showCrmFields[field.key] ? <EyeOff className='h-7 w-7' /> : <Eye className='h-7 w-7' />}
													</Button>
												</div>
											) : (
												field.value
											)
										) : (
											<span className='text-gray-400 italic'>
												{field.placeholder}
											</span>
										)}
									</span>
								</div>
							</div>
						)}
					</div>
				))}
				
				
				{/* Save/Cancel buttons when editing in profile mode */}
				{isEditing && !isOnboarding && (
					<div className='flex justify-end gap-3 pt-4 border-t border-gray-200'>
						<Button
							variant='outline'
							onClick={handleCancel}
							className='border-gray-300 text-gray-600 hover:bg-gray-50'>
							<X className='h-4 w-4 mr-2' />
							Cancel
						</Button>
						<Button
							onClick={handleSave}
							className='bg-gray-900 hover:bg-gray-800 text-white'>
							<Check className='h-4 w-4 mr-2' />
							Save Changes
						</Button>
					</div>
				)}
				
				{/* Save button for onboarding */}
				{isOnboarding && (
					<div className='pt-6 mt-6 border-t border-gray-200'>
						<Button
							onClick={handleOnboardingSave}
							className='w-full bg-gray-900 hover:bg-gray-800 text-white'>
							<Check className='h-4 w-4 mr-2' />
							Save & Continue
						</Button>
					</div>
				)}
			</Card.Content>
		</Card>
	);
};

export default SocialMediaCard;